import React, { FC, FunctionComponent, useCallback, useRef, useState } from 'react';
import cx from 'classnames';
import loginStyles from './styles/login.scss';
import resetPassword from './styles/password-reset.scss';
import logo from '../../assets/images/rtspro-logo-blue.svg';
import { TextInput, Button, Icon, Snackbar, Spinner } from 'shamrock-clover-ui';
import { useNavigate, useSearchParams } from 'react-router-dom';
import sentinelService from '../../services/sentinel.service';
import { getDeviceType } from '../../utils/utils';
import { useTranslation } from 'react-i18next';
import { isMobile } from './utils';
import { RootState } from 'bootstrap';
import { useSelector } from 'react-redux';
const Config = require('Config');

interface ErrorTexts {
  password?: {
    characterCount: boolean;
    capitalLetter: boolean;
    numberOrSpecial: boolean;
  };
}

const RenderPasswordValidationLine: FC<{
  validationInstruction: string;
  isValid: boolean;
  password: string;
  errorTexts: ErrorTexts;
}> = ({ validationInstruction, isValid = false, password, errorTexts }) => (
  <li>
    <div className={resetPassword.validationIcon}>
      {password !== '' && errorTexts.password && (isValid ? <Icon icon="checkGreen" /> : <Icon icon="redX" />)}
    </div>
    {validationInstruction}
  </li>
);

const PasswordReset: FunctionComponent = () => {
  const password = useRef('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [submitErrorText, setSubmitErrorText] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const [errorTexts, setErrorTexts] = useState<ErrorTexts>({
    password: {
      characterCount: true,
      capitalLetter: true,
      numberOrSpecial: true,
    },
  });
  const { t } = useTranslation();
  const isMobileValue = isMobile();
  const features = useSelector<RootState, string[]>((state) => state.app.features);
  const isNewPasswordResetUIenabled = features.includes('CPUA-4998-WEB');
  const isUniversalLinkEnabled = features.includes('cb07431ef6713159ea018cfe46575df241f90dfc017c7a6b458f7cfb8b8834d0');
  const getStylesClass = (key: string) => (isNewPasswordResetUIenabled ? resetPassword[key] : loginStyles[key]);

  const backToLogin = () => {
    if (getDeviceType() === 'Desktop') {
      navigate('/');
    } else {
      window.location.href = isUniversalLinkEnabled ? Config.backToLoginBranchUrl : 'rtspro://back_to_login';
    }
  };
  const validateSubmit = () =>
    password.current.length === 0 ||
    errorTexts.password?.characterCount ||
    errorTexts.password?.capitalLetter ||
    errorTexts.password?.numberOrSpecial ||
    password.current !== confirmPassword;

  const buildValidationErrors = (field: string, value: string): Partial<ErrorTexts> => {
    const passwordErrors = Object.assign({}, errorTexts.password);
    passwordErrors.characterCount = value.length < 8;
    passwordErrors.capitalLetter = !value.match(/[A-Z]/);
    passwordErrors.numberOrSpecial = !value.match(/[^a-zA-Z]/);
    return { password: passwordErrors };
  };

  const submit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      try {
        //password
        setIsSubmitting(true);
        await sentinelService.changePassword({
          newPassword: confirmPassword,
          token: searchParams.get('token'),
        });
        setSubmitSuccess(true);
      } catch (error) {
        setSubmitError(true);
        error?.response?.data?.passwordTooSimilar ?
          setSubmitErrorText(t('authentication.password_too_similar_error')) :
          setSubmitErrorText(t('there_was_an_error_changing_your_password'));
      } finally {
        setIsSubmitting(false);
      }
    },
    [confirmPassword],
  );

  const validateField = (...args: [string, string]) => setErrorTexts({ ...buildValidationErrors(...args) });

  const ClosedHideEyeBtn = (
    <Button
      className={getStylesClass('iconBtn')}
      type="button"
      buttonStyle="text"
      width="26px"
      height="24px"
      onClick={() => setShowPassword(!showPassword)}
      icon={showPassword ? <Icon icon="eyeShow" /> : <Icon icon="eyeHide" />}
    />
  );

  const OldCloseHideEyeBtn = (
    <div className={loginStyles.loginPasswordIcon} onClick={() => setShowPassword(!showPassword)}>
      {showPassword ? <Icon icon="eyeShow" /> : <Icon icon="eyeHide" />}
    </div>
  );

  const isLogoRendered = (isNewPasswordResetUIenabled && isMobileValue) || !isNewPasswordResetUIenabled;

  if (!Boolean(token)) navigate('/');

  return (
    <div className={getStylesClass('container')}>
      <div className={getStylesClass('loginWindow')}>
        <div className={getStylesClass('loginContainer')}>
          {isLogoRendered && (
            <div className={cx([getStylesClass('passwordResetMargin'), getStylesClass('rtsProLogo')])}>
              <img src={logo} alt="rtspro_blue_logo" />
            </div>
          )}
          <div className={getStylesClass('loginHeader')}>
            <h2>{t('set_your_new_password')}</h2>
          </div>
          <form onSubmit={submit}>
            <div className={getStylesClass('loginPasswordContainer')}>
              <TextInput
                label={t('password')}
                className={getStylesClass('loginPasswordText')}
                variant={isMobileValue && isNewPasswordResetUIenabled ? 'outlined' : 'filled'}
                inputType={showPassword ? 'text' : 'password'}
                value={password.current}
                autoComplete={false}
                onChange={({ target }) => {
                  password.current = target.value;
                  validateField('password', password.current);
                }}
              />
              {isNewPasswordResetUIenabled ? ClosedHideEyeBtn : OldCloseHideEyeBtn}
            </div>
            <div className={getStylesClass('loginPasswordContainer')}>
              <TextInput
                label={t('confirm_password')}
                variant={isMobileValue && isNewPasswordResetUIenabled ? 'outlined' : 'filled'}
                inputType={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                autoComplete={false}
                onChange={({ target }) => setConfirmPassword(target.value)}
              />
              {isNewPasswordResetUIenabled ? ClosedHideEyeBtn : OldCloseHideEyeBtn}
            </div>
            <div className={getStylesClass('helper_text')}>
              <span>{t('password_must_contain')}:</span>
              <ul>
                <RenderPasswordValidationLine
                  errorTexts={errorTexts}
                  password={password.current}
                  validationInstruction={t('at_least_eight_characters')}
                  isValid={!errorTexts.password?.characterCount}
                />
                <RenderPasswordValidationLine
                  errorTexts={errorTexts}
                  password={password.current}
                  validationInstruction={t('a_capital_letter')}
                  isValid={!errorTexts.password?.capitalLetter}
                />
                <RenderPasswordValidationLine
                  errorTexts={errorTexts}
                  password={password.current}
                  validationInstruction={t('a_number_or_special_character')}
                  isValid={!errorTexts.password?.numberOrSpecial}
                />
                <RenderPasswordValidationLine
                  errorTexts={errorTexts}
                  password={password.current}
                  validationInstruction={t('both_fields_must_match')}
                  isValid={password.current.length > 0 && password.current === confirmPassword}
                />
              </ul>
            </div>
            <div
              className={isNewPasswordResetUIenabled ? resetPassword.buttonContainer : resetPassword.oldButtonContainer}
            >
              <div className={resetPassword.button}>
                <Button
                  buttonStyle={!isMobileValue && isNewPasswordResetUIenabled ? 'outlined' : 'text'}
                  type="button"
                  onClick={backToLogin}
                  width={isNewPasswordResetUIenabled ? '164' : '145'}
                  height={isNewPasswordResetUIenabled ? '44' : '40'}
                >
                  {t('back_to_login')}
                </Button>
              </div>
              <div className={resetPassword.button}>
                <Button
                  buttonStyle="solid"
                  type="submit"
                  disabled={isSubmitting || validateSubmit() || submitSuccess}
                  width={isNewPasswordResetUIenabled ? '186' : '100%'}
                  height={isNewPasswordResetUIenabled ? '44' : '40'}
                  data-testid="create-password-button"
                >
                  {isSubmitting ? <Spinner color="white" size="25" /> : t('confirm')}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
      <Snackbar
        open={submitError || submitSuccess}
        position="bottomCenter"
        duration={5000}
        onClose={() => submitError && setSubmitError(false)}
        message={
          submitSuccess
            ? t('your_password_was_successfully_changed_you_will_receive_email_confirmation')
            : submitErrorText
        }
        variant={submitError ? 'error' : 'success'}
      />
    </div>
  );
};

export default PasswordReset;
